// source: template-project@7007a338a apps/backoffice/src/components/ui/shadcn/table.tsx (COPY)
// Vendored from shadcn/ui — MODIFIED for the lab `table-standard` recipe
// (audit §V11 step 2). Three edits, and they repaint every table in the app:
//   - base `text-xs` → `text-sm` (the lab body size)
//   - `TableCell` `py-2` → `py-3`
//   - `TableHead` gains `bg-muted/30` (the header tint; authored as `bg-surface-1/30`
//     and retoned in the final review — see the `// MODIFIED:` note at `TableHead`)
// `TableHead`'s uppercase 11px recipe was already correct before E5a.
//
// Measured (computed style, 20-row tables built from these exact classes against
// the app's real stylesheet). Row height depends on cell CONTENT, so it is quoted
// as a range, not a number — the "28→36px" this comment used to claim matched
// nothing and contradicted the E5a commit body:
//   cell font          12 → 14px      (text-xs → text-sm)
//   cell padding       8  → 12px      per side (py-2 → py-3)
//   row, one line      32.5 → 44.5px  (+8 padding, +4 line-height)
//   row, + status pill 38.5 → 50.5px
//   row, two-line cell 48.5 → 64.5px  (+8 padding, +4 line-height ×2)
//   20-row body        979.5 → 1299.5px (+33%) for the two-line case
// The E5a commit body's "row height 49→65px / 979→1299px" is the two-line row,
// and is correct.
// This is deliberately done at the primitive so the ~150 inline `TableHead`
// className overrides across 63 files become deletable in E5b, rather than
// per-table.
// Do NOT regenerate with `npx shadcn add table --overwrite`.
import * as React from "react"

import { cn } from "@/lib/utils"
import { useDragToScroll } from "@/hooks/useDragToScroll"

type TableProps = React.HTMLAttributes<HTMLTableElement> & {
  /** Enable grab-cursor drag-to-scroll on the overflow wrapper. */
  dragToScroll?: boolean
  containerClassName?: string
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, dragToScroll = false, containerClassName, ...props }, ref) => {
    const containerRef = React.useRef<HTMLDivElement>(null)
    useDragToScroll(containerRef, dragToScroll)

    return (
      // min-w-0 + max-w-full: flex ancestors default to min-width:auto and
      // grow with wide tables, so overflow never engages and scroll "breaks".
      <div
        ref={containerRef}
        className={cn(
          "relative w-full min-w-0 max-w-full overflow-x-auto overflow-y-visible",
          dragToScroll && "overscroll-x-contain",
          containerClassName
        )}
      >
        <table
          ref={ref}
          // MODIFIED: base `text-xs` -> `text-body-sm` (the v3 table cell, 13px / 18px; th stays 11px).
          className={cn("w-full caption-bottom text-body-sm", className)}
          {...props}
        />
      </div>
    )
  }
)
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    )}
    {...props}
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      // MODIFIED: `text-[11px]` -> `text-2xs` (ds sweep, Phase 4 Wave B; audit §V7 typography
      // recipe) and `TableHead` gained the lab header tint (audit §V11).
      // MODIFIED (final review): that tint was `bg-surface-1/30`, which renders LIGHT in dark
      // mode — the app-owned dark halves in index.css target the bare `.bg-surface-1`, and an
      // opacity modifier emits a different class (`.bg-surface-1\/30`) that no `.dark` rule
      // covers, so it kept the preset's literal light hex over the dark card. `--muted` themes
      // itself, so `bg-muted/30` is correct in both. ESLint now bans the whole pattern.
      "h-auto bg-muted/30 px-3 py-2.5 text-left align-middle text-2xs font-semibold uppercase tracking-wider text-muted-foreground [&:has([role=checkbox])]:px-2",
      className
    )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    // MODIFIED: cell padding `py-2` -> `py-3` (lab `table-standard` row rhythm; audit §V11).
    // Checkbox cells: balanced px-2 (not px-3 + pr-0) so a size-4 control
    // stays square inside the narrow select column under table-fixed.
    className={cn("px-3 py-3 align-middle [&:has([role=checkbox])]:px-2", className)}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
